<template>
  <ToDoWidgetButton
    id="toDoWidgetButton"
    :count="openedCount"
    :isActive="isShowWidget"
    @onClick="onToggleWidget"
  />
  <Teleport
    v-if="isTeleport"
    to="#toDoWidget"
  >
    <Transition
      @beforeEnter="beforeEnter"
      @beforeLeave="beforeLeave"
    >
      <div
        v-if="isShowWidget"
        ref="toDoWidgetEl"
        class="to-do-widget content is-60-40"
      >
        <div>&nbsp;</div>
        <Transition @beforeEnter="beforeEnterToDo">
          <ToDoComponent
            v-if="showToDo"
            :showCloseButton="true"
            :selectedToDoInList="selectedToDoInList"
            @onHideToDoWidget="onHideToDo"
            @mousedown.stop
          />
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Watch } from "vue-property-decorator"
import { gsap } from "gsap"
import { Action, Getter } from "s-vuex-class"

import ToDoComponent from "@/components/ToDo/index.vue"
import ToDoWidgetButton from "@/components/ToDo/ToDoWidgetButton.vue"

import { ResourceType, ServerSendEventMessage, TodoItemChangeDto } from "@/models/common"
import { ToDo } from "@/models/ToDo"
import { emitterDeleteByKey, emitterOn } from "@/plugins/mitt-emitter"
import { Actions, Getters } from "@/store"

const components = {
  ToDoComponent,
  ToDoWidgetButton
}

@Options({
  name: "ToDoWidget",
  components
})
export default class ToDoWidget extends Vue {
  @Getter(Getters.GET_OPENED_TODO_COUNT) readonly openedCount: number
  @Getter(Getters.THEME_IS_DARK) isDark: boolean
  @Getter(Getters.SUBSCRIPTION_MESSAGE) readonly subscriptionMessage: (
    type: ResourceType
  ) => ServerSendEventMessage[] | undefined

  @Action(Actions.GET_OPENED_TODO_COUNT) getOpenedToDoCount: () => void
  @Action(Actions.SET_OPENED_TODO_COUNT_FROM_SSE) setOpenedToDoCountFromSSE: (count: number) => void
  @Action(Actions.SET_TODO) setToDo: (toDo: ToDo) => void
  @Action(Actions.GET_TODO) getToDo: ({
    toDoId,
    withoutUpdate
  }: {
    toDoId: string
    withoutUpdate: boolean
  }) => Promise<ToDo>
  @Action(Actions.RESET_TO_DO) resetToDo: () => void

  isShowWidget = false
  isTeleport = false
  showToDo = false
  backGroundAnimation = gsap.timeline({
    paused: true
  })
  toDoAnimation = gsap.timeline({
    paused: true
  })
  layoutElement: HTMLElement | null = null
  selectedToDoInList: ToDo | null = null

  get showToDoByQuery() {
    return this.$route.query?.toDo || this.$route.query?.toDoItemId
  }

  mounted() {
    this.getOpenedToDoCount()
    emitterOn("addToDo", this.onAddToDo)
    this.layoutElement = document.getElementById("layout")
    this.onShowToDoByQuery()
  }

  onAddToDo(toDo: ToDo) {
    this.setToDo(toDo)
    this.onToggleWidget()
  }

  onToggleWidget() {
    if (!this.isShowWidget) {
      this.onShowWidgetUpdate(true)
    } else {
      this.onHideToDo()
    }
  }

  onShowWidgetUpdate(value) {
    this.isTeleport = value
    this.$nextTick(() => {
      this.isShowWidget = value
    })
  }

  beforeEnter(toDoWidgetEl) {
    this.backGroundAnimation = gsap
      .timeline({
        paused: true
      })
      .to(toDoWidgetEl, {
        background: "rgba(129, 136, 155, 0)",
        duration: 0
      })
      .to(toDoWidgetEl, {
        background: !this.isDark ? "rgba(129, 136, 155, 0.50)" : "rgba(89, 90, 93, 0.60)",
        duration: 0.3
      })
      .play()
      .eventCallback("onComplete", () => {
        this.showToDo = true
        if (this.layoutElement) {
          this.layoutElement.addEventListener("mousedown", this.onLayoutMouseDown)
        }
      })
  }

  beforeEnterToDo(toDoWidgetEl) {
    this.toDoAnimation = gsap
      .timeline({
        paused: true
      })
      .to(
        toDoWidgetEl,
        {
          duration: 0,
          translateY: "100%",
          opacity: 0
        },
        0
      )
      .to(toDoWidgetEl, {
        opacity: 1,
        duration: 0
      })
      .to(toDoWidgetEl, {
        duration: 0.3,
        translateY: 0
      })
      .play()
  }

  beforeLeave() {
    this.backGroundAnimation.reverse(0)
  }

  /** Closes the widget on a click outside it; toasts float above it, so closing a toast keeps the widget open. */
  onLayoutMouseDown(event: MouseEvent) {
    if ((event.target as HTMLElement).closest(".toasts__wrapper")) return
    this.onHideToDo()
  }

  onHideToDo() {
    this.toDoAnimation.reverse(0).eventCallback("onReverseComplete", () => {
      this.onShowWidgetUpdate(false)
      this.showToDo = false
      if (this.layoutElement) {
        this.layoutElement.removeEventListener("mousedown", this.onLayoutMouseDown)
      }
      this.resetToDo()
    })
    this.selectedToDoInList = null
  }

  @Watch("showToDoByQuery")
  async onShowToDoByQuery() {
    if (this.showToDoByQuery) {
      const query = { ...this.$route.query }
      if (query.toDoItemId) {
        this.selectedToDoInList = await this.getToDo({
          toDoId: query.toDoItemId as string,
          withoutUpdate: true
        })
      }
      delete query.toDo
      delete query.toDoItemId
      if (query.commentId) query.comment = query.commentId
      delete query.commentId
      await this.$router.replace({ query })
      this.onShowWidgetUpdate(true)
    }
  }

  get todoItemChangeMessage() {
    return this.subscriptionMessage("TODO_ITEM_CHANGE")
  }

  @Watch("todoItemChangeMessage")
  onTodoItemChange(messages: ServerSendEventMessage[] | undefined) {
    const todoItemChangeMessages = messages as TodoItemChangeDto[] | undefined

    if (todoItemChangeMessages?.length) {
      const latestMessage = todoItemChangeMessages[todoItemChangeMessages.length - 1]
      this.setOpenedToDoCountFromSSE(latestMessage.countOfUncompletedTodoItems)
    }
  }

  beforeUnmount() {
    emitterDeleteByKey("addToDo")
  }
}
</script>
